import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import * as S from './styles';
import { BasicDialog } from '../../../../../components/newStudent/Dialog';
import NegotiationTerm from '../NegotiationTerm';
import NegotiationSummary from '../NegotiationSummary';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import FieldFormik from '../../../../../components/newStudent/FieldFormik';
import { Formik } from 'formik';
import TitleDialogMobile from '../../../../../components/newStudent/Title/TitleDialogMobile';
import Divider from '../../../../../components/common/Divider';
import { CardLeftColor } from '../../../../../components/newStudent/Card';
import {
  cleanCache,
  formatCurrency,
  formatDate,
  formatPercent,
} from '../../../../../util/common/appUtil';
import {
  financialServiceNewStudent,
  IInfoDebtAndNegotiationBillets,
} from '../../../../../services/newStudent/financial/financial.service';
import { IPaymentType } from '../../../../../services/newStudent/helper/helper.service';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../components/newStudent/ProviderNewStudent';
import { company } from '../../../../../variables';
import { INegotiationSimulation } from '../../../../../interfaces/newStudent/negotiation.interface';

interface Props {
  dataInvoice: IInfoDebtAndNegotiationBillets[];
  paymentType: IPaymentType[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export default function DebtNegotiationMobile({
  dataInvoice,
  paymentType,
  isOpen,
  setIsOpen,
}: Props) {
  const { setIsLoading, student } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [readed, setReaded] = useState<boolean>();
  const [simulation, setSimulation] = useState<INegotiationSimulation | null>(
    null,
  );
  const [simulationVersion, setSimulationVersion] = useState(0);
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    number | undefined
  >(0);
  const [selectedOptionsParcels, setSelectedOptionsParcels] = useState<
    number | undefined
  >(0);
  const [selectedQuantity, setSelectedQuantity] = useState(dataInvoice.length);

  const optionsPaymentType = useMemo(() => {
    return [
      { title: '[ Selecione a forma de pagamento ]', value: undefined },
      ...(paymentType.length > 0
        ? paymentType
            .filter((item) => item.description)
            .map((item) => ({
              title: item.description!.split(' ')[0],
              value: item.id,
            }))
        : []),
    ];
  }, [paymentType]);

  const option = useMemo(() => {
    return selectedPaymentType
      ? paymentType.find((item) => item.id === selectedPaymentType)
      : undefined;
  }, [selectedPaymentType, paymentType]);

  const optionsParcels = useMemo(() => {
    return [
      { title: '[ Número de parcelas ]', value: undefined },
      ...(option?.parcels
        ? option.parcels.map((item) => ({
            title: item.quantity + 'X',
            value: item.quantity,
          }))
        : []),
    ];
  }, [option]);

  useEffect(() => {
    setSelectedQuantity(dataInvoice.length);
  }, [dataInvoice]);

  const selectedIds = useMemo(
    () => dataInvoice.slice(0, selectedQuantity).map((item) => item.id),
    [dataInvoice, selectedQuantity],
  );

  const quantityOptions = useMemo(
    () =>
      dataInvoice.map((_, index) => ({
        title: index === 0 ? '1 mensalidade' : `${index + 1} mensalidades`,
        value: index + 1,
      })),
    [dataInvoice],
  );

  const itemById = useMemo(() => {
    return new Map((simulation?.items ?? []).map((item) => [item.id, item]));
  }, [simulation]);

  const paymentLabel = simulation?.isCreditCard
    ? 'Cartão de Crédito'
    : (optionsPaymentType.find((item) => item.value === selectedPaymentType)
        ?.title ?? '');

  useEffect(() => {
    if (!selectedPaymentType || !selectedOptionsParcels) {
      setSimulation(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const { result, data } =
          await financialServiceNewStudent.showNegotiationSimulation({
            id: selectedIds,
            paymentType: selectedPaymentType,
            parcel: selectedOptionsParcels,
          });
        if (!cancelled) {
          setSimulation(result ? data : null);
        }
      } catch (error) {
        if (!cancelled) {
          setSimulation(null);
        }
        throw new Error(String(error));
      } finally {
        setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedPaymentType, selectedOptionsParcels, simulationVersion, selectedIds]);

  const handleSend = async () => {
    if (!simulation || !selectedPaymentType || !selectedOptionsParcels) {
      return;
    }

    setIsLoading(true);
    try {
      const { result } = await financialServiceNewStudent.installmentsNegotiation(
        {
          id: selectedIds,
          paymentType: selectedPaymentType,
          parcel: selectedOptionsParcels,
          total: simulation.totalAmount,
          acceptedTerms: true,
        },
      );

      if (result) {
        cleanCache('dataContracts');
        localStorage.removeItem('dataStudent');
      } else {
        setReaded(false);
        setSimulationVersion((current) => current + 1);
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const validPaymentType = (id?: number) => {
    const validate = optionsPaymentType.find((item) => item.value === +id!);
    if (validate) {
      if (
        validate.title.toLowerCase().includes('pix') ||
        validate.title.toLowerCase().includes('bole')
      ) {
        setSelectedOptionsParcels(1);
        return false;
      }
    }
    return true;
  };

  const discountTitle = simulation
    ? `Desconto (${formatPercent(simulation.discountPercent)})`
    : 'Desconto';
  const interestTitle = simulation
    ? `Juros (${formatPercent(simulation.interestPercent)} a.m.)`
    : 'Juros';
  const fineTitle = simulation
    ? `Multa (${formatPercent(simulation.finePercent)})`
    : 'Multa';
  const increaseTitle = simulation
    ? `Acréscimo (${formatPercent(simulation.operatorFeePercent)})`
    : 'Acréscimo';

  return (
    <BasicDialog isOpen={isOpen} setIsOpen={setIsOpen} fullScreen scroll="body">
      <TitleDialogMobile setIsOpen={setIsOpen} title={'Histórico Financeiro'} />
      <S.Container>
        <S.TitlePage>Negociação</S.TitlePage>
        <Divider padding />
        <Formik
          initialValues={{
            selectQuantity: dataInvoice.length,
            selectPaymentType: undefined,
            selectOptionsParcels: undefined,
          }}
          onSubmit={() => {}}
          enableReinitialize
        >
          {({ handleChange, setFieldValue, values }) => (
            <S.DivTitleSelect>
              {dataInvoice.length > 1 ? (
                <>
                  <S.H6>Mensalidades a negociar:</S.H6>
                  <FieldFormik
                    name="selectQuantity"
                    type="select"
                    value={values.selectQuantity}
                    onChange={(e) => {
                      handleChange(e);
                      setSelectedQuantity(+e.target.value);
                      setSimulation(null);
                      setReaded(undefined);
                    }}
                    options={quantityOptions}
                    widthSize="xxlarge"
                  />
                </>
              ) : null}
              <S.H6>Forma de Pagamento: </S.H6>
              <FieldFormik
                name="selectPaymentType"
                type="select"
                value={values.selectPaymentType}
                onChange={(e) => {
                  handleChange(e);
                  setSelectedPaymentType(+e.target.value);
                  setSelectedOptionsParcels(undefined);
                  setSimulation(null);
                  setReaded(undefined);
                  setFieldValue('selectOptionsParcels', 0);
                }}
                options={optionsPaymentType}
                widthSize="xxlarge"
              />
              {!!selectedPaymentType &&
                validPaymentType(values.selectPaymentType) && (
                  <FieldFormik
                    name="selectOptionsParcels"
                    type="select"
                    value={values.selectOptionsParcels}
                    onChange={(e) => {
                      handleChange(e);
                      setReaded(undefined);
                      setSimulation(null);
                      setSelectedOptionsParcels(+e.target.value);
                    }}
                    options={optionsParcels}
                    widthSize="large"
                  />
                )}
            </S.DivTitleSelect>
          )}
        </Formik>
        <Divider padding color="gray02" />
        {dataInvoice.map((item, index) => {
          if (index >= selectedQuantity) {
            return (
              <S.Card key={item.id}>
                <CardLeftColor color="gray03" heightSize="fullHeight">
                  <S.DivCard>
                    <S.CardContent>
                      <S.KeyValue>
                        <S.Key>Referência:</S.Key>
                        <S.Value color="gray03">{item.invoiceReference}</S.Value>
                      </S.KeyValue>
                      {item.courseName ? (
                        <S.CourseName>{item.courseName}</S.CourseName>
                      ) : null}
                      <S.KeyValue>
                        <S.Key>Valor:</S.Key>
                        <S.Value color="gray03">
                          {formatCurrency(item.value)}
                        </S.Value>
                      </S.KeyValue>
                      <S.KeyValue>
                        <S.Key>Vencimento:</S.Key>
                        <S.Value color="gray03">
                          {formatDate(item.dueDate)}
                        </S.Value>
                      </S.KeyValue>
                      <S.KeyValue>
                        <S.Key>Não incluída nesta negociação</S.Key>
                      </S.KeyValue>
                    </S.CardContent>
                  </S.DivCard>
                </CardLeftColor>
                <Divider padding color="gray02" />
              </S.Card>
            );
          }
          const simulated = itemById.get(item.id);
          return (
            <S.Card key={item.id}>
              <CardLeftColor color="danger" heightSize="fullHeight">
                <S.DivCard>
                  <S.CardContent>
                    <S.KeyValue>
                      <S.Key>Referência:</S.Key>
                      <S.Value color="danger">{item.invoiceReference}</S.Value>
                    </S.KeyValue>
                    {item.courseName ? (
                      <S.CourseName>{item.courseName}</S.CourseName>
                    ) : null}
                    <S.KeyValue>
                      <S.Key>Valor:</S.Key>
                      <S.Value color="danger">
                        {formatCurrency(simulated?.value ?? item.value)}
                      </S.Value>
                    </S.KeyValue>
                    <S.KeyValue>
                      <S.Key>Vencimento:</S.Key>
                      <S.Value color="danger">
                        {formatDate(simulated?.dueDate ?? item.dueDate)}
                      </S.Value>
                    </S.KeyValue>
                    <S.KeyValue>
                      <S.Key>{discountTitle}:</S.Key>
                      <S.Value color="danger">
                        {formatCurrency(simulated ? simulated.discountAmount : 0)}
                      </S.Value>
                    </S.KeyValue>
                    <S.KeyValue>
                      <S.Key>{interestTitle}:</S.Key>
                      <S.Value color="danger">
                        {formatCurrency(simulated?.interestAmount ?? 0)}
                      </S.Value>
                    </S.KeyValue>
                    <S.KeyValue>
                      <S.Key>{fineTitle}:</S.Key>
                      <S.Value color="danger">
                        {formatCurrency(simulated?.fineAmount ?? 0)}
                      </S.Value>
                    </S.KeyValue>
                    <S.KeyValue>
                      <S.Key>{increaseTitle}:</S.Key>
                      <S.Value color="danger">
                        {formatCurrency(simulated?.financialIncreaseAmount ?? 0)}
                      </S.Value>
                    </S.KeyValue>
                    <S.KeyValue>
                      <S.Key>Valor Final:</S.Key>
                      <S.Value color="danger">
                        {formatCurrency(simulated?.totalAmount ?? item.value)}
                      </S.Value>
                    </S.KeyValue>
                  </S.CardContent>
                </S.DivCard>
              </CardLeftColor>
              <Divider padding color="gray02" />
            </S.Card>
          );
        })}
        {simulation ? (
          <S.Card>
            <CardLeftColor color="danger" heightSize="fullHeight">
              <S.DivCard>
                <S.CardContent>
                  <S.KeyValue>
                    <S.Key>TOTAL</S.Key>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>Valor:</S.Key>
                    <S.Value color="danger">
                      {formatCurrency(simulation.debtAmount)}
                    </S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>{discountTitle}:</S.Key>
                    <S.Value color="danger">
                      {formatCurrency(simulation.discountAmount)}
                    </S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>{interestTitle}:</S.Key>
                    <S.Value color="danger">
                      {formatCurrency(simulation.interestAmount)}
                    </S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>{fineTitle}:</S.Key>
                    <S.Value color="danger">
                      {formatCurrency(simulation.fineAmount)}
                    </S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>{increaseTitle}:</S.Key>
                    <S.Value color="danger">
                      {formatCurrency(simulation.financialIncreaseAmount)}
                    </S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>Valor Final:</S.Key>
                    <S.Value color="danger">
                      {formatCurrency(simulation.totalAmount)}
                    </S.Value>
                  </S.KeyValue>
                </S.CardContent>
              </S.DivCard>
            </CardLeftColor>
            <Divider padding color="gray02" />
          </S.Card>
        ) : null}
        {simulation ? (
          <NegotiationSummary
            simulation={simulation}
            paymentLabel={paymentLabel}
          />
        ) : null}
        <S.DivSend>
          <S.CheckboxDiv>
            <S.TermButton onClick={() => setIsModalOpen(true)}>
              Termo de Aceite.
            </S.TermButton>
            {simulation ? (
              <FieldFormik
                name="readed"
                placeholder={simulation?.acceptanceText ?? ''}
                onChange={(e) => {
                  setReaded(e.target.checked);
                }}
                disabled={!simulation}
                checked={readed}
                type="checkbox"
                widthSize="fullWidth"
                notLimitHeight
              />
            ) : null}
            <S.Button>
              <ButtonSimple
                color="primary"
                widthSize="fullWidth"
                disabled={!simulation || !readed}
                onClick={() => handleSend()}
              >
                CONFIRMAR NEGOCIAÇÃO
              </ButtonSimple>
            </S.Button>
          </S.CheckboxDiv>
        </S.DivSend>
        <NegotiationTerm
          name={student.dataStudent.name}
          company={company ?? 'Famart'}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          ableBackdropClick
          buttonLabel="CONFIRMAR NEGOCIAÇÃO"
          simulation={simulation}
          paymentLabel={paymentLabel}
        />
      </S.Container>
    </BasicDialog>
  );
}
